import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  ColumnLayout,
  Container,
  DatePicker,
  FormField,
  Header,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { MultiselectProps } from "@amzn/awsui-components-react/polaris/multiselect";
import { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import { useNavigate, useSearchParams } from "react-router-dom";
import Page from "components/page";
import PermissionBoundary from "components/permission-boundary";
import { useBreadcrumbs } from "features/breadcrumbs/hooks";
import { useNotifications } from "features/notifications/hooks";
import { useAppSelector } from "app/store";
import {
  useCreatePrincipalDatasetMutation,
  useGetTeamConfigQuery,
} from "services/adi/endpoints";
import { normalizeTeamId } from "./utils";

const DatasetCreateWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contextId = searchParams.get("contextId");
  const contextType = searchParams.get("contextType");
  const preCountry = searchParams.get("country");
  const preEntities = searchParams.get("entities");
  const preTaxTypes = searchParams.get("taxTypes");
  const preStates = searchParams.get("states");
  const preStartDate = searchParams.get("startDate");
  const preEndDate = searchParams.get("endDate");
  const preName = searchParams.get("name");
  const { updateBreadcrumbs } = useBreadcrumbs();
  const { addNotification } = useNotifications();

  const teamName = useAppSelector((state) => state.audits.teamName);
  const uiContent = useAppSelector((state) => state.uiContent);
  const normalizedTeamId = normalizeTeamId(teamName);

  const { data: teamConfigData } = useGetTeamConfigQuery(
    { teamId: normalizedTeamId },
    { skip: !normalizedTeamId },
  );

  const [createPrincipalDataset, { isLoading: isSubmitting }] =
    useCreatePrincipalDatasetMutation();

  const countryOptions = uiContent.countryOptions || [];
  const entityOptions = uiContent.entityOptions || [];
  const taxTypeOptions = useMemo(
    () =>
      (teamConfigData?.taxTypes || []).map((taxType) => ({
        label: taxType,
        value: taxType,
      })),
    [teamConfigData?.taxTypes],
  );
  // TeamConfig states are formatted "Full Name: CODE" (e.g. "Quebec: QC").
  // Show the full name, submit the code. Split on the first ":".
  const statesOptions = useMemo(
    () =>
      (teamConfigData?.states || []).map((s) => {
        const i = s.indexOf(":");
        return i === -1
          ? { label: s.trim(), value: s.trim() }
          : { label: s.slice(0, i).trim(), value: s.slice(i + 1).trim() };
      }),
    [teamConfigData?.states],
  );

  const [datasetName, setDatasetName] = useState(preName || "");
  const [selectedCountry, setSelectedCountry] =
    useState<SelectProps.Option | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<
    MultiselectProps.Option[]
  >([]);
  const [selectedTaxTypes, setSelectedTaxTypes] = useState<
    MultiselectProps.Option[]
  >([]);
  const [selectedStates, setSelectedStates] = useState<
    MultiselectProps.Option[]
  >([]);
  const [startDate, setStartDate] = useState(preStartDate || "");
  const [endDate, setEndDate] = useState(preEndDate || "");
  const [contextInitialized, setContextInitialized] = useState(false);
  const [teamConfigInitialized, setTeamConfigInitialized] = useState(false);

  // Snapshot of the values auto-filled from the audit context at
  // initialization, so the "Pre-populated from audit" summary keeps
  // describing the original pre-fill even after the user edits the form.
  const prefilledEntitiesRef = useRef<MultiselectProps.Option[]>([]);
  const prefilledTaxTypesRef = useRef<MultiselectProps.Option[]>([]);

  // Pre-fill the audit-context fields (country, entities) once their option
  // lists (from uiContent) are available. Kept independent of team config so
  // these are never held hostage by a slow-loading or unresolved team config.
  // Guarded so it never overwrites the user's later edits.
  useEffect(() => {
    if (contextInitialized) return;
    // Wait for each option list we actually need to match against, so a
    // slower-loading list doesn't get locked out by initializing too early.
    if (preCountry && countryOptions.length === 0) return;
    if (preEntities && entityOptions.length === 0) return;

    const matchedCountry = preCountry
      ? countryOptions.find((o) => o.value === preCountry) || null
      : null;

    const matchedEntities = preEntities
      ? entityOptions.filter((o) => {
          // Direct match on the full string (handles single composite keys
          // and entity names that contain commas, e.g. "Amazon.com.ca, ULC||7H")
          if (
            o.value === preEntities ||
            o.description === preEntities ||
            o.value?.includes(`||${preEntities}`) ||
            o.value?.split("||")[1] === preEntities
          ) {
            return true;
          }
          // Comma-separated codes (multi-entity flows from "Copy to new")
          return preEntities
            .split(",")
            .some(
              (code) =>
                o.description === code.trim() ||
                o.value === code.trim() ||
                o.value?.split("||")[1] === code.trim(),
            );
        })
      : [];

    if (matchedCountry) setSelectedCountry(matchedCountry);
    if (matchedEntities.length > 0) setSelectedEntities(matchedEntities);

    prefilledEntitiesRef.current = matchedEntities;

    setContextInitialized(true);
  }, [
    contextInitialized,
    countryOptions,
    entityOptions,
    preCountry,
    preEntities,
  ]);

  // Pre-fill the team-config-derived fields (tax types, states) once team
  // config resolves. Separate from the context effect above so these fields
  // load whenever team config arrives (not on first render) without blocking
  // the context fields, and so we only mark this done AFTER team config is
  // available — otherwise states could be marked initialized before the list
  // exists and would never pre-fill. When there is no team (query skipped),
  // teamConfigData stays undefined and these simply never pre-fill.
  useEffect(() => {
    if (teamConfigInitialized) return;
    if (!teamConfigData) return;

    const matchedTaxTypes = preTaxTypes
      ? taxTypeOptions.filter((o) => preTaxTypes.split(",").includes(o.value!))
      : [];

    const matchedStates = preStates
      ? statesOptions.filter((o) => preStates.split(",").includes(o.value!))
      : [];

    // Empty states means "no jurisdiction filter", so only tax type defaults.
    // Named but unmatched stays empty, so validation forces a choice.
    const defaultTaxTypes =
      matchedTaxTypes.length > 0
        ? matchedTaxTypes
        : preTaxTypes
          ? []
          : taxTypeOptions;

    if (defaultTaxTypes.length > 0) setSelectedTaxTypes(defaultTaxTypes);
    if (matchedStates.length > 0) setSelectedStates(matchedStates);

    prefilledTaxTypesRef.current = matchedTaxTypes;

    setTeamConfigInitialized(true);
  }, [
    teamConfigInitialized,
    teamConfigData,
    taxTypeOptions,
    statesOptions,
    preTaxTypes,
    preStates,
  ]);

  useEffect(() => {
    updateBreadcrumbs([
      { text: "Gaia", href: "/" },
      { text: "Data and insights", href: "/data-insights/" },
      { text: "TAR data extraction", href: "/data-insights/tar-extraction" },
    ]);
  }, []);

  const isFormValid = (): boolean => {
    if (!datasetName.trim()) return false;
    if (!selectedCountry) return false;
    if (selectedEntities.length === 0) return false;
    if (selectedTaxTypes.length === 0) return false;
    if (!startDate || !endDate) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || !teamConfigData) return;

    const payload = {
      teamId: normalizedTeamId,
      parentId: contextId || normalizedTeamId,
      gaiaContextId: contextId || "STANDALONE",
      gaiaContextType: contextId ? contextType || "AUDIT" : "STANDALONE",
      countryCode: selectedCountry?.value || "",
      datasetName: datasetName.trim(),
      dateRange: {
        startDate,
        endDate,
      },
      columns: teamConfigData.columns.map((col) => col.name),
      teamConfigVersion: teamConfigData.version,
      filters: {
        sor_code: selectedEntities.map((e) => e.value!),
        tax_type: selectedTaxTypes.map((t) => t.value!),
        states: selectedStates.map((s) => s.value!),
      },
    };

    try {
      const result = await createPrincipalDataset(payload).unwrap();
      navigate(`/datasets/${result.principalId}`);
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        "Failed to create dataset";
      addNotification({ type: "error", header: message });
    }
  };

  const prePopulatedSummary = useMemo(() => {
    // Render once the context fields have pre-filled; the tax-type segment
    // fills in when team config resolves (below in deps). Gating on
    // teamConfigInitialized too would hide the summary entirely when the team
    // is unresolved/skipped, re-coupling it to team config.
    if (!contextInitialized) return null;
    if (!contextId || !preCountry) return null;
    const entityStr = prefilledEntitiesRef.current
      .map((e) => e.label || e.value)
      .join(", ");
    const taxStr = prefilledTaxTypesRef.current.map((t) => t.value).join("/");
    const startYear = preStartDate?.split("-")[0] || "";
    const endYear = preEndDate?.split("-")[0] || "";
    // Skip unsupplied segments, so this never reads "US | | | 2026-2026".
    const parts = [
      preCountry,
      entityStr,
      taxStr,
      startYear && endYear ? `${startYear}-${endYear}` : "",
    ].filter(Boolean);
    return `Fields auto-filled from ${parts.join(" | ")}.`;
  }, [
    contextId,
    preCountry,
    // Recompute once initialization populates the refs; the summary then
    // stays fixed to the original pre-fill regardless of later user edits.
    contextInitialized,
    teamConfigInitialized,
    preStartDate,
    preEndDate,
  ]);

  return (
    <PermissionBoundary allowMemberOnly={true}>
      <Page
        type="page"
        title="TAR data extraction — Create principal dataset"
        description="Create a new principal dataset by defining the audit scope."
        hasBreadcrumbs={true}
        hasInfo={false}
      >
        <div style={{ maxWidth: "960px" }}>
          <SpaceBetween size="l">
            <Container
              header={<Header variant="h2">Dataset configuration</Header>}
            >
              <SpaceBetween size="s">
                {prePopulatedSummary && (
                  <Alert type="success" header="Pre-populated from audit">
                    {prePopulatedSummary}
                  </Alert>
                )}

                <FormField
                  label="Dataset name"
                  description="A descriptive name for this principal dataset."
                >
                  <Input
                    value={datasetName}
                    onChange={({ detail }) => setDatasetName(detail.value)}
                    placeholder="Enter dataset name"
                  />
                </FormField>

                {!preCountry && (
                  <FormField label="Country">
                    <Select
                      selectedOption={selectedCountry}
                      onChange={({ detail }) =>
                        setSelectedCountry(detail.selectedOption)
                      }
                      options={countryOptions}
                      placeholder="Select country"
                    />
                  </FormField>
                )}

                <ColumnLayout columns={2}>
                  <FormField
                    label="Legal entity (entity code)"
                    description="Select one or more legal entities."
                  >
                    <Multiselect
                      selectedOptions={selectedEntities}
                      onChange={({ detail }) =>
                        setSelectedEntities(
                          detail.selectedOptions as MultiselectProps.Option[],
                        )
                      }
                      options={entityOptions}
                      placeholder="Select entities"
                      tokenLimit={3}
                      enableSelectAll
                      i18nStrings={{ selectAllText: "Select all" }}
                    />
                  </FormField>

                  <FormField
                    label="Tax type"
                    description="Select one or more tax types."
                  >
                    <Multiselect
                      selectedOptions={selectedTaxTypes}
                      onChange={({ detail }) =>
                        setSelectedTaxTypes(
                          detail.selectedOptions as MultiselectProps.Option[],
                        )
                      }
                      options={taxTypeOptions}
                      placeholder="Select tax types"
                      tokenLimit={3}
                      enableSelectAll
                      i18nStrings={{ selectAllText: "Select all" }}
                    />
                  </FormField>
                </ColumnLayout>

                <ColumnLayout columns={1}>
                  <FormField
                    label="State/Province"
                    description="Select one or more state(s)/province(s)."
                  >
                    <Multiselect
                      selectedOptions={selectedStates}
                      onChange={({ detail }) =>
                        setSelectedStates(
                          detail.selectedOptions as MultiselectProps.Option[],
                        )
                      }
                      options={statesOptions}
                      placeholder="Select states"
                      tokenLimit={3}
                      enableSelectAll
                      i18nStrings={{ selectAllText: "Select all" }}
                    />
                  </FormField>
                </ColumnLayout>

                <ColumnLayout columns={2}>
                  <FormField
                    label="Lock date — start"
                    description="Start of audit period (up to 5 years)"
                  >
                    <DatePicker
                      value={startDate}
                      onChange={({ detail }) => setStartDate(detail.value)}
                      placeholder="YYYY-MM-DD"
                      openCalendarAriaLabel={(selectedDate) =>
                        selectedDate
                          ? `Choose start date, selected date is ${selectedDate}`
                          : "Choose start date"
                      }
                    />
                  </FormField>

                  <FormField
                    label="Lock date — end"
                    description="End of audit period"
                  >
                    <DatePicker
                      value={endDate}
                      onChange={({ detail }) => setEndDate(detail.value)}
                      placeholder="YYYY-MM-DD"
                      openCalendarAriaLabel={(selectedDate) =>
                        selectedDate
                          ? `Choose end date, selected date is ${selectedDate}`
                          : "Choose end date"
                      }
                    />
                  </FormField>
                </ColumnLayout>
              </SpaceBetween>
            </Container>

            <SpaceBetween size="s" direction="horizontal" alignItems="center">
              <Button
                variant="link"
                onClick={() => navigate("/data-insights/")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!isFormValid() || isSubmitting}
                loading={isSubmitting}
              >
                Create principal dataset
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </div>
      </Page>
    </PermissionBoundary>
  );
};

export default DatasetCreateWizard;
